import React, { useState } from "react";
import { Alert } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useGetTriviaAchievementCatalogQuery } from "../store/api/mlbApi";
import { useTriviaStats } from "../hooks/useTriviaStats";
import { useTriviaLeaderboards } from "../hooks/useTriviaLeaderboards";
import { useTriviaDaily } from "../hooks/useTriviaDaily";
import { useTriviaArcade } from "../hooks/useTriviaArcade";
import { getSportFromPath } from "../utils/sportWorkspace";
import { getTriviaStartingCopy } from "../utils/triviaArcade";
import TriviaHub from "../components/trivia/TriviaHub";
import TriviaGameScreen from "../components/trivia/TriviaGameScreen";
import TriviaResultsScreen from "../components/trivia/TriviaResultsScreen";
import TriviaCustomConfigSheet from "../components/trivia/TriviaCustomConfigSheet";
import TriviaStartingScreen from "../components/trivia/TriviaStartingScreen";

const Trivia = () => {
  const [hubError, setHubError] = useState(null);
  const location = useLocation();
  const sport = getSportFromPath(location.pathname) || "mlb";

  const {
    userStats,
    achievements,
    gameHistory,
    historyPagination,
    setHistoryPage,
    isLoading,
    hasError,
    refetch: refetchStats,
  } = useTriviaStats();

  const { data: catalogData } = useGetTriviaAchievementCatalogQuery();
  const {
    globalLeaderboard,
    streakLeaderboard,
    speedLeaderboard,
    isLoading: isLoadingLeaderboards,
    refetch: refetchLeaderboards,
  } = useTriviaLeaderboards();

  const daily = useTriviaDaily(sport);
  const arcade = useTriviaArcade({ userStats, sport });

  const handleGameExit = async () => {
    arcade.returnToHub();
    await Promise.all([
      refetchStats(),
      refetchLeaderboards(),
      daily.refetchDaily(),
      daily.refetchLeaderboard(),
    ]);
  };

  const handleDailyPlay = async () => {
    setHubError(null);
    const result = await arcade.startDailyPlay();
    if (result?.success === false && result.error) {
      setHubError(result.error);
      await daily.refetchDaily();
    }
  };

  if (arcade.view === "starting" || (arcade.view === "playing" && !arcade.currentQuestion)) {
    const copy = getTriviaStartingCopy(arcade.gameMode);
    return (
      <TriviaStartingScreen
        title={copy.title}
        subtitle={copy.subtitle}
        reducedMotion={arcade.reducedMotion}
      />
    );
  }

  if (arcade.view === "playing") {
    return (
      <TriviaGameScreen
        sport={sport}
        question={arcade.currentQuestion}
        currentIndex={arcade.currentQuestionIndex}
        total={arcade.questionCount}
        score={arcade.displayScore}
        timeLeft={arcade.timeLeft}
        timeLimit={arcade.timeLimit}
        streak={arcade.displayStreak}
        phase={arcade.phase}
        lastResult={arcade.lastResult}
        selectedAnswer={arcade.selectedAnswer}
        toastQueue={arcade.toastQueue}
        reducedMotion={arcade.reducedMotion}
        showQuitDialog={arcade.showQuitDialog}
        error={arcade.error}
        onSelectAnswer={arcade.selectAnswer}
        onQuit={() => arcade.setShowQuitDialog(true)}
        onConfirmQuit={arcade.confirmQuit}
        onCancelQuit={() => arcade.setShowQuitDialog(false)}
        onDismissToast={arcade.dismissToast}
        onSkipFeedback={arcade.continueAfterFeedback}
      />
    );
  }

  if (arcade.view === "results" && arcade.results) {
    return (
      <TriviaResultsScreen
        results={arcade.results}
        startLevel={arcade.startLevel}
        startXp={arcade.startXp}
        reducedMotion={arcade.reducedMotion}
        sport={sport}
        onPlayAgain={() => {
          if (arcade.gameMode === "quick") {
            arcade.startQuickPlay();
          } else if (arcade.lastCustomConfig) {
            arcade.startCustomPlay(arcade.lastCustomConfig);
          }
        }}
        onBack={handleGameExit}
        onViewLeaderboard={() => {
          arcade.setCommunityTab("daily");
          handleGameExit();
        }}
      />
    );
  }

  if (hasError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load trivia data. Please try again later.
      </Alert>
    );
  }

  return (
    <>
      <TriviaHub
        userStats={userStats}
        daily={daily.daily}
        globalLeaderboard={globalLeaderboard}
        streakLeaderboard={streakLeaderboard}
        speedLeaderboard={speedLeaderboard}
        dailyLeaderboard={daily.leaderboard}
        achievements={achievements}
        achievementCatalog={catalogData?.achievements}
        gameHistory={gameHistory}
        historyPagination={historyPagination}
        onHistoryPageChange={setHistoryPage}
        isLoading={isLoading}
        isLoadingLeaderboards={isLoadingLeaderboards}
        communityTab={arcade.communityTab}
        onCommunityTabChange={arcade.setCommunityTab}
        onQuickPlay={arcade.startQuickPlay}
        onCustom={arcade.openCustom}
        onDailyPlay={handleDailyPlay}
        onViewDailyBoard={() => arcade.setCommunityTab("daily")}
        reducedMotion={arcade.reducedMotion}
        error={hubError || arcade.error}
        onDismissError={() => setHubError(null)}
      />
      <TriviaCustomConfigSheet
        open={arcade.view === "config"}
        onClose={arcade.returnToHub}
        onStart={arcade.startCustomPlay}
        isStarting={arcade.isStarting}
        sport={sport}
      />
    </>
  );
};

export default Trivia;
